// ============================================================================
// ExecutiveGrantsTab — onglet CE (grants comite executif, GEST-VISIBILITY-MODEL)
// ============================================================================
// Liste des courriels accordes, formulaire d'ajout, revocation soft. Tokens @gs/ui.

import { useState } from 'react';
import { Loader2, Plus, Trash2, UserCheck } from 'lucide-react';
import {
  ConfirmDialog, DataTable, EmptyState, QueryError, SkeletonCard, GS_COMPONENTS, type Column,
} from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useUpsertExecutiveGrant, useRevokeExecutiveGrant, adminExecutiveGrantErrorCode,
  type AdminExecutiveGrantsResponse, type ExecutiveGrantRow,
} from '../../hooks/useAdminExecutiveGrants';
import { SectionHeader } from './SectionHeader';

export function ExecutiveGrantsTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminExecutiveGrantsResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertExecutiveGrant();
  const revoke = useRevokeExecutiveGrant();
  const [email, setEmail] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ExecutiveGrantRow | null>(null);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.executiveGrants.loadError')} onRetry={onRetry} />;
  }

  const activeGrants = data.grants.filter((g) => g.isActive);
  const pending = upsert.isPending || revoke.isPending;

  function submitGrant(e: React.FormEvent) {
    e.preventDefault();
    setErrorCode(null);
    const trimmed = email.trim();
    if (!trimmed) return;
    upsert.mutate(trimmed, {
      onSuccess: () => setEmail(''),
      onError: (err) => setErrorCode(adminExecutiveGrantErrorCode(err)),
    });
  }

  function confirmRevoke() {
    if (!revokeTarget) return;
    setErrorCode(null);
    revoke.mutate(revokeTarget.id, {
      onSuccess: () => setRevokeTarget(null),
      onError: (err) => {
        setErrorCode(adminExecutiveGrantErrorCode(err));
        setRevokeTarget(null);
      },
    });
  }

  const columns: Column<ExecutiveGrantRow>[] = [
    {
      key: 'email',
      header: t('gest:admin.executiveGrants.columns.email'),
      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{row.granteeEmail}</span>
      ),
    },
    {
      key: 'status',
      header: t('gest:admin.executiveGrants.columns.status'),
      render: (row) => (
        <span className={row.isActive ? 'text-green-700 dark:text-green-400' : 'text-slate-500'}>
          {row.isActive
            ? t('gest:admin.executiveGrants.statusActive')
            : t('gest:admin.executiveGrants.statusRevoked')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.isActive ? (
          <button
            type="button"
            disabled={pending}
            onClick={() => setRevokeTarget(row)}
            className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1.5`}
          >
            <Trash2 size={14} aria-hidden="true" />
            {t('gest:admin.executiveGrants.revoke')}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.executiveGrants.title')}
        description={t('gest:admin.executiveGrants.description')}
      />

      <form onSubmit={submitGrant} className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-4 sm:flex-row sm:items-end`}>
        <div className="min-w-0 flex-1">
          <label htmlFor="ce-grant-email" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.executiveGrants.addLabel')}
          </label>
          <input
            id="ce-grant-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('gest:admin.executiveGrants.addPlaceholder')}
            className={`${GS_COMPONENTS.input} mt-1 w-full touch-manipulation`}
            disabled={pending}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={pending || !email.trim()}
          className={`${GS_COMPONENTS.btnAction} touch-manipulation inline-flex items-center justify-center gap-2`}
        >
          {upsert.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
          {t('gest:admin.executiveGrants.add')}
        </button>
      </form>

      {errorCode ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {t(`gest:admin.executiveGrants.errors.${errorCode}`, { defaultValue: errorCode })}
        </p>
      ) : null}

      {activeGrants.length === 0 ? (
        <EmptyState
          icon={<UserCheck size={48} aria-hidden="true" />}
          title={t('gest:admin.executiveGrants.empty')}
          description={t('gest:admin.executiveGrants.emptyHint')}
        />
      ) : (
        <DataTable
          rowKey={(row) => row.id}
          columns={columns}
          data={data.grants}
          emptyMessage={t('gest:admin.executiveGrants.empty')}
        />
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        variant="danger"
        title={t('gest:admin.executiveGrants.revokeConfirmTitle')}
        message={t('gest:admin.executiveGrants.revokeConfirmBody', {
          email: revokeTarget?.granteeEmail ?? '',
        })}
        confirmLabel={t('gest:admin.executiveGrants.revoke')}
        cancelLabel={t('gest:admin.executiveGrants.revokeConfirmCancel')}
        isLoading={revoke.isPending}
        onConfirm={confirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
